import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LegalShell = ({ title, updated, children, testId }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FFF7E6]" data-testid={testId}>
      <div className="max-w-2xl mx-auto px-5 py-8">
        <button onClick={() => navigate(-1)} data-testid="legal-back" className="flex items-center gap-1 text-[#111827]/60 font-semibold mb-6"><ArrowLeft className="w-5 h-5" /> Back</button>
        <h1 className="text-3xl font-bold font-head tracking-tight">{title}</h1>
        <p className="text-sm text-[#111827]/50 mt-1 mb-6">Safety notices · {updated}</p>
        <div className="space-y-5 text-[#111827]/80 leading-relaxed">{children}</div>
        <p className="text-xs text-[#111827]/40 mt-10 border-t border-[#111827]/10 pt-4">HumNest is in active development and does not replace police, ambulance, fire, or other official emergency services. In a real emergency, dial 112.</p>
      </div>
    </div>
  );
};

const H = ({ children }) => <h2 className="text-xl font-bold font-head text-[#111827] pt-2">{children}</h2>;
const P = ({ children }) => <p>{children}</p>;
const LI = ({ children }) => <li className="ml-1">{children}</li>;

export const Terms = () => (
  <LegalShell title="Terms of Service" updated="Milestone 1" testId="legal-terms-screen">
    <P>By using HumNest you agree to use the platform responsibly and lawfully. HumNest is a neighbourhood coordination tool, not an emergency dispatch service.</P>
    <H>Acceptable use</H>
    <P>You must provide accurate information, respect other members, and never misuse emergency features. False emergency alerts are strictly prohibited.</P>
    <H>Platform status</H>
    <P>Features are under active development and may change or be unavailable. Do not rely on HumNest as your only means of getting help.</P>
    <H>Accounts</H>
    <P>You are responsible for keeping your account secure. We may restrict or suspend accounts that violate our Community Guidelines.</P>
    <H>Liability</H>
    <P>HumNest is provided "as is" during active development. We are not liable for outcomes of any emergency. Always contact official services for real emergencies.</P>
  </LegalShell>
);

export const Privacy = () => (
  <LegalShell title="Privacy Policy" updated="Milestone 1" testId="legal-privacy-screen">
    <H>What data we collect</H>
    <P>We collect your name, email, mobile number, preferred language, optional date of birth and profile photo, your chosen home community, trusted contacts, and Care Circle profiles you create.</P>
    <H>Why we collect it</H>
    <P>This information helps neighbours recognise you, lets trusted contacts be reached in an emergency, and helps coordinate the right response within your community.</P>
    <H>Home community vs live emergency location</H>
    <P>Your home community is where you live and is stored separately from any live location. Your live location may differ and is only used when you explicitly trigger or share an emergency.</P>
    <H>No continuous tracking</H>
    <P>HumNest does not use continuous background location tracking in this release. Location is requested only when the related feature is actively used.</P>
    <H>Who can access emergency data</H>
    <P>Your private profile, trusted contacts, and Care Circle notes are visible only to you. Community admins may see only approved operational data for their assigned community. No user can see another user's trusted contacts, and exact residential addresses are never shown publicly.</P>
    <H>Requesting deletion</H>
    <P>You may request deletion of your account and associated data at any time by contacting the HumNest support team; we will remove your records from active systems.</P>
  </LegalShell>
);

export const BetaSafety = () => (
  <LegalShell title="Safety Disclaimer" updated="Milestone 1" testId="legal-beta-screen">
    <P className="font-semibold text-[#DC2626]">HumNest is in active development and does NOT replace police, ambulance, fire services, or any official emergency service.</P>
    <H>For real emergencies</H>
    <P>Always contact official services directly: Police 100, Fire 101, Ambulance 102/108, National Emergency 112.</P>
    <H>What is not yet active</H>
    <P>Emergency coordination, responder dispatch, live incident response and volunteer integration are not yet available. HumNest does not currently integrate police, ambulance, or volunteer services.</P>
    <H>Use responsibly</H>
    <P>Do not depend on HumNest as your only means of getting help during this release. Test features are clearly labelled and do not alert real responders.</P>
  </LegalShell>
);

export const CommunityGuidelines = () => (
  <LegalShell title="Community Guidelines" updated="Milestone 1" testId="legal-guidelines-screen">
    <P>HumNest exists to build trust between neighbours. To keep everyone safe, the following are strictly prohibited:</P>
    <ul className="list-disc pl-6 space-y-1">
      <LI>Hate speech</LI>
      <LI>Religious or caste-based targeting</LI>
      <LI>Harassment</LI>
      <LI>Threats</LI>
      <LI>Political incitement</LI>
      <LI>Doxxing or sharing private addresses</LI>
      <LI>Misinformation</LI>
      <LI>False emergency alerts</LI>
      <LI>Discrimination of any kind</LI>
    </ul>
    <H>Our commitment</H>
    <P className="font-semibold">HumNest does not group, rank, or segregate users based on religion, caste, ethnicity, or political identity. Everyone is treated as an equal neighbour.</P>
    <H>Enforcement</H>
    <P>Violations may lead to warnings, restrictions, or removal from the platform. Emergency misuse is treated with the highest severity.</P>
  </LegalShell>
);
